import { useEffect, useRef } from 'preact/hooks';
import { nextSessionIndex } from '../utils/carousel';

const SWIPE_THRESHOLD     = 80;   // px horizontal travel for a full swipe
const FAST_FLICK_VELOCITY = 0.5;  // px/ms — fast flick commits with less travel
const FAST_FLICK_MIN_DX   = 35;   // px — min travel even for a fast flick
const MAX_VERT_DRIFT      = 55;   // px vertical drift allowed before cancelling
                                   // (55 not 40: chat content is vertically scrolled,
                                   // hand tilt during a horizontal swipe is expected)
const MIN_VELOCITY        = 0.3;  // px/ms — discard very slow drags

interface Options {
  openTabs: string[];
  currentSessionId: string | null;
  switchTab: (sessionId: string) => void;
  enabled: boolean;           // only active when chat panel is visible
  /** Pass via callback ref: `ref={setContainerEl}` on the host element. */
  containerEl: HTMLElement | null;
  onSwipe?: (direction: 'prev' | 'next') => void;
  /** Called during drag with the constrained display offset (px). 0 = reset. */
  onDrag?: (offset: number) => void;
  /** Called on touch end. `committed` = threshold met and session will switch. */
  onDragEnd?: (committed: boolean) => void;
  /** Called once when drag distance first exceeds 70% of SWIPE_THRESHOLD — haptic cue. */
  onNearCommit?: () => void;
}

/**
 * Detects horizontal swipe gestures on the chat panel and cycles through
 * open session tabs as a TRUE WRAPAROUND CAROUSEL (Phase 2 of
 * plans/agents-drawer-carousel-reorg.md — there is no boundary).
 *
 * Swipe right → previous session, wrapping from openTabs[0] → openTabs[n-1]
 * Swipe left  → next session,     wrapping from openTabs[n-1] → openTabs[0]
 *
 * The cyclic index math is the pure helper `nextSessionIndex` (utils/carousel.ts) so
 * the same logic the SE simulator exercises is the logic that runs here. A single tab
 * (n <= 1) is a no-op. There is no "go to list" destination anymore — the session list
 * lives in the Agents bottom-nav drawer, reached via the bottom bar, not by swiping.
 *
 * Guards:
 * - Only fires when enabled (chat view active).
 * - Cancels if vertical drift exceeds MAX_VERT_DRIFT (user is scrolling).
 * - Requires minimum velocity to distinguish flick from accidental drag.
 * - Ignores touches that start on interactive elements (input, button, etc.).
 */
export function useSwipeSession({
  openTabs,
  currentSessionId,
  switchTab,
  enabled,
  containerEl,
  onSwipe,
  onDrag,
  onDragEnd,
  onNearCommit,
}: Options) {
  const startX         = useRef(0);
  const startY         = useRef(0);
  const startTime      = useRef(0);
  const cancelled      = useRef(false);
  const tracking       = useRef(false);
  const nearCommitFired = useRef(false);

  // Stable refs so event handlers always see current values without re-registering
  const openTabsRef         = useRef(openTabs);
  openTabsRef.current       = openTabs;
  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;

  useEffect(() => {
    const el = containerEl;
    if (!el) return;
    console.debug('[swipe] attached to', el);

    function onTouchStart(e: TouchEvent) {
      // Reset state
      tracking.current       = false;
      cancelled.current      = false;
      nearCommitFired.current = false;

      if (!enabled) return;

      // Ignore touches on interactive elements; NOT .chat-area — we allow
      // swiping from the message list; vertical scroll intent is detected in
      // onTouchMove and the gesture is cancelled before any preventDefault.
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, button, a, select, [role="textbox"], .ssh-terminal')) return;

      tracking.current  = true;
      startX.current    = e.touches[0].clientX;
      startY.current    = e.touches[0].clientY;
      startTime.current = Date.now();
    }

    function onTouchMove(e: TouchEvent) {
      if (!tracking.current || cancelled.current) return;

      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;

      // If vertical drift is too large → user is scrolling, cancel
      if (Math.abs(dy) > MAX_VERT_DRIFT) {
        cancelled.current = true;
        onDrag?.(0); // reset any live visual offset
        return;
      }

      // If clearly horizontal, prevent the page from scrolling sideways
      if (Math.abs(dx) > 15 && Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
      }

      // No rubber-band: a cycle has no boundary, so every drag tracks 1:1 and there
      // is always a wrapped neighbour to slide toward.
      const displayDx = dx;

      onDrag?.(displayDx);

      // Near-commit haptic: fire once when drag exceeds 70% of threshold
      if (!nearCommitFired.current && Math.abs(displayDx) > SWIPE_THRESHOLD * 0.7) {
        nearCommitFired.current = true;
        onNearCommit?.();
      }
    }

    function onTouchEnd(e: TouchEvent) {
      nearCommitFired.current = false;
      if (!tracking.current || cancelled.current) {
        tracking.current = false;
        onDragEnd?.(false);
        return;
      }
      tracking.current = false;

      const dx       = e.changedTouches[0].clientX - startX.current;
      const dy       = e.changedTouches[0].clientY - startY.current;
      const dt       = Math.max(1, Date.now() - startTime.current);
      const velocity = Math.abs(dx) / dt;

      const isFastFlick = velocity >= FAST_FLICK_VELOCITY && Math.abs(dx) >= FAST_FLICK_MIN_DX;
      const isFullSwipe = Math.abs(dx) >= SWIPE_THRESHOLD && velocity >= MIN_VELOCITY;

      if ((!isFastFlick && !isFullSwipe) || Math.abs(dy) > MAX_VERT_DRIFT) {
        onDragEnd?.(false);
        return;
      }

      const tabs = openTabsRef.current;
      const idx  = tabs.indexOf(currentSessionIdRef.current ?? '');
      if (idx === -1) {
        onDragEnd?.(false);
        return;
      }

      // True wraparound carousel: swipe RIGHT (dx>0) → previous (wraps 0 → n-1),
      // swipe LEFT (dx<0) → next (wraps n-1 → 0). Cyclic math in the pure helper.
      const dir: 'prev' | 'next' = dx > 0 ? 'prev' : 'next';
      const targetIdx = nextSessionIndex(idx, tabs.length, dir);
      if (targetIdx !== idx) {
        switchTab(tabs[targetIdx]);
        onSwipe?.(dir);
        onDragEnd?.(true);
      } else {
        // Single tab (or no movement target) — nothing to switch to; bounce back.
        onDragEnd?.(false);
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });

    return () => {
      console.debug('[swipe] detached');
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  // openTabs / currentSessionId are tracked via refs — not referenced directly in effect body.
  // containerEl MUST be in deps: it changes from null→element after loading screen unmounts.
  }, [containerEl, enabled, switchTab, onSwipe, onDrag, onDragEnd, onNearCommit]);
}
